import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import i18n from '@/lib/i18n/config';

/**
 * The zone every timestamp in the console is shown in. TEPCO operates in Saudi Arabia, the
 * leads, publish schedules and audit trail are all about Riyadh working hours, and a reviewer
 * abroad must read the same hour a colleague in Riyadh does. Saudi Arabia keeps no DST, but the
 * helpers below resolve the offset per instant anyway rather than hard-coding +03:00.
 */
export const SITE_TIME_ZONE = 'Asia/Riyadh';

/** Active date-fns locale, tracking the i18next chrome language (spec §5.3 / §6). */
function activeDateLocale() {
  return i18n.language === 'ar' ? ar : enUS;
}

/** Human-readable byte size with thin-space unit (DESIGN numeric voice). */
export function formatBytes(bytes: number): string {
  if (!bytes) return '0\u2009B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)}\u2009${units[i]}`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: activeDateLocale() });
  } catch {
    return '—';
  }
}

/** The wall-clock parts of an instant in `timeZone`, or null for an unknown zone. */
function zonedParts(date: Date, timeZone: string): Record<string, string> | null {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const map: Record<string, string> = {};
    for (const p of parts) map[p.type] = p.value;
    map.hour = String(Number(map.hour) % 24).padStart(2, '0');
    return map;
  } catch {
    return null;
  }
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `yyyy-MM-dd HH:mm` in the site zone (Asia/Riyadh), whatever the reader's own zone. */
export function formatDateTime(iso: string | null | undefined): string {
  const date = parse(iso);
  const p = date && zonedParts(date, SITE_TIME_ZONE);
  if (!p) return '—';
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/**
 * `yyyy-MM-dd`. A calendar date the API sends as a bare `YYYY-MM-DD` (a call date) is a day,
 * not an instant, and is shown as-is; an instant is read in the site zone.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const date = parse(iso);
  const p = date && zonedParts(date, SITE_TIME_ZONE);
  return p ? `${p.year}-${p.month}-${p.day}` : '—';
}

/**
 * A localized wall-clock time in a named zone (the site zone by default): "11 Oct 2026, 14:05"
 * in English, Arabic month names with Latin digits in Arabic. An unknown zone falls back to the
 * site zone rather than throwing (`Intl` rejects a bad `timeZone` with a RangeError).
 */
export function formatInZone(iso: string | null | undefined, timeZone: string = SITE_TIME_ZONE): string {
  const date = parse(iso);
  if (!date) return '—';
  const locale = i18n.language === 'ar' ? 'ar' : 'en-GB';
  for (const zone of [timeZone, SITE_TIME_ZONE]) {
    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone: zone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        numberingSystem: 'latn',
      }).format(date);
    } catch {
      /* An invalid IANA name: try again with the site zone. */
    }
  }
  return formatDateTime(iso);
}

/**
 * How a zone is named to a reader: `Asia/Riyadh` becomes `Riyadh (GMT+3)`, the offset resolved
 * at the given instant.
 */
export function zoneLabel(timeZone: string = SITE_TIME_ZONE, at?: string | null): string {
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
  const date = (at && parse(at)) || new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(date);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${city} (${offset})` : city;
  } catch {
    return city;
  }
}

/** The offset of `timeZone` at an instant, in milliseconds (wall clock read back minus UTC). */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  if (!p) throw new RangeError(`unknown time zone ${timeZone}`);
  const asUTC = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asUTC - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * An instant → the `YYYY-MM-DDTHH:mm` a `<input type="datetime-local">` wants, read in the site
 * zone rather than the browser's. `''` when there is no instant.
 */
export function instantToZonedInput(iso: string | null | undefined, timeZone: string = SITE_TIME_ZONE): string {
  const date = parse(iso);
  const p = date && zonedParts(date, timeZone);
  return p ? `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` : '';
}

/**
 * The inverse: a wall clock typed in the site zone → the RFC 3339 instant the API stores, or
 * null for a malformed value. The offset is applied twice so a zone with DST lands right on the
 * changeover days (Riyadh has none; the helper stays correct if the zone ever changes).
 */
export function zonedInputToInstant(local: string, timeZone: string = SITE_TIME_ZONE): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const naive = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  try {
    let ts = naive - zoneOffsetMs(new Date(naive), timeZone);
    ts = naive - zoneOffsetMs(new Date(ts), timeZone);
    return new Date(ts).toISOString();
  } catch {
    return null;
  }
}
